import RaftGenericHelper from "@/lib/raft_generic_helper";
import TimeInputHelper from "./TimeInputHelper";

/**
 * A class that formats a field value via data from its entity view display.
 */
const EntityViewDisplayFieldFormatters = {
  /**
   * Formats a field given an entity view display.
   *
   * @param {String} value - The field data value.
   * @param {Object} displayData - The entity field view display.
   *
   * @return {String} The formatted field.
   */
  fieldFormatter(value, displayData) {
    const type = displayData.display_settings.type;
    const method = this.formatterTypeMap()[type];
    if (typeof this[method] === "undefined") {
      return value;
    }
    return this[method](value, displayData);
  },

  /**
   * Returns a map for the field type to the formatting method.
   *
   * @return {Object} The formatter type map.
   */
  formatterTypeMap() {
    return {
      string: "formatDefault",
      number_integer: "formatDefault",
      list_default: "formatList",
      list_key: "formatList",
      boolean: "formatBoolean",
      link: "formatLink",
      entity_reference_label: "formatEntityReferenceLabel",
      text_default: "formatRichText",
      explainfield_default: "formatExplainField",
      explainfield: "formatExplainField",
      time_formatter: "formatTimeField",
      timestamp: "formatDateTimeField",
    };
  },

  /**
   * Formatter method that simply returns the value.
   *
   * @param {String} value - The field data value.
   *
   * @return {String} The formatted field value.
   */
  formatDefault(value) {
    return value;
  },

  formatList(value, displayData) {
    if (value === null) {
      return "";
    }
    // @todo Refactor to use retrieveAllowedValue to avoid full restructuring of the allowedValues.
    const orgAllowedVals = displayData.definition.settings.allowed_values;
    let allowedVals = {};
    // Check to see if we have an array to convert to a simple mapping for use later.
    if (Array.isArray(orgAllowedVals)) {
      orgAllowedVals.forEach((opt) => {
        allowedVals[opt.value] = opt.text;
      });
    } else {
      allowedVals = orgAllowedVals;
    }
    if (typeof value === "string" || typeof value === "number") {
      return allowedVals[value];
    }
    const humanList = [];
    if (Array.isArray(value)) {
      value.forEach((key) => {
        humanList.push(allowedVals[key]);
      });
    } else {
      // @todo Why are we getting an object here, and what property should we be looking at?
      console.error("Invalid value type:");
      console.dir(value);
    }
    return humanList.join(", ");
  },

  /**
   * Formats a string field.
   *
   * @param {String} value - The field data value.
   * @param {Object} displayData - The entity view display.
   *
   * @return {String} The formatted field value.
   */
  formatBoolean(value, displayData) {
    const booleanLabels = displayData.definition.settings;
    if (value) {
      return booleanLabels.on_label;
    }
    return booleanLabels.off_label;
  },

  /**
   * Formats a string field.
   *
   * @param {Object} value - The field data value.
   * @param {Object} displayData - The entity view display.
   *
   * @return {String|Object} The formatted field value.
   */
  formatLink(value, displayData) {
    if (!value?.uri) {
      return "";
    }
    if (displayData.display_settings?.settings?.url_plain) {
      return value.uri;
    }
    let text = value?.title;
    if (displayData.display_settings?.settings?.url_only || !text) {
      text = value.uri;
    }
    let rel = "";
    const relSetting = displayData.display_settings?.settings?.rel;
    // Unchecking the checkbox in the Drupal UI gives us "0" not "" as setting.
    if (relSetting && relSetting !== "0") {
      rel = ` rel="${relSetting}"`;
    }
    let target = "";
    const targetSetting = displayData.display_settings?.settings?.target;
    if (targetSetting && targetSetting !== "0") {
      target = ` target="${targetSetting}"`;
    }
    return { v_html: `<a href="${value.uri}"${rel}${target}>${text}</a>` };
  },

  /**
   * Formats an entity reference field.
   *
   * @param {String|Array} value - The field data value.
   *
   * @return {String|Object} The formatted field value.
   */
  formatEntityReferenceLabel(value) {
    if (Array.isArray(value) && value.length === 0) {
      return "";
    }
    if (Array.isArray(value) && value.length > 1) {
      const labelList = value.map(
        (ref) => "<li>" + this.formatSingleEntityReferenceLabel(ref) + "</li>",
      );
      return { v_html: "<ul>" + labelList.join("\n") + "</ul>" };
    }
    if (Array.isArray(value)) {
      value = value[0];
    }
    return this.formatSingleEntityReferenceLabel(value);
  },

  /**
   * Formats a single entity reference value.
   *
   * @param {String} value - The field data value.
   *
   * @return {String} The formatted field value.
   */
  formatSingleEntityReferenceLabel(value) {
    const field_names = ["title", "label", "display_name", "name"];
    if (value && typeof value === "object") {
      for (const key of field_names) {
        if (value.hasOwnProperty(key)) {
          return value[key];
        }
      }
    }
    return value;
  },

  /**
   * Formats a string field.
   *
   * @param {Object} value - The field data value.
   *
   * @return {Object} Puts HTML in a v_html key for BaseTable to process.
   */
  formatRichText(value) {
    if (value !== null && typeof value.processed !== "undefined") {
      return { v_html: value.processed };
    }
    return "";
  },

  /**
   * Formats a string field.
   *
   * @param {Object} value - The field data value.
   *
   * @return {Object} Puts HTML in a v_html key for BaseTable to process.
   */
  formatHtml(value) {
    if (value !== null && typeof value.processed !== "undefined") {
      return { v_html: value.processed };
    }
    if (value !== null && typeof value.value !== "undefined") {
      return { v_html: value.value };
    }
    return "";
  },

  /**
   * Formats a explain field.
   *
   * @param {Object} value - The field data value.
   * @param {Object} displayData - The entity view display.
   *
   * @return {String} The formatted field value.
   */
  formatExplainField(value, displayData) {
    if (value === null) {
      return "";
    }
    let label = null;
    if (Array.isArray(value)) {
      const labels = [];
      value.forEach((valueObject) => {
        labels.push(
          this.retrieveAllowedValue(
            valueObject.value,
            displayData.definition.settings.allowed_values,
          ),
        );
      });
      label = labels.join(", ");
    } else {
      label = this.retrieveAllowedValue(
        value.value,
        displayData.definition.settings.allowed_values,
      );
    }
    const explainKey = displayData.definition.settings.explain_key;
    if (value.value === explainKey) {
      return label + ": " + value.explain;
    }
    return label;
  },

  /**
   * Format a time field.
   *
   * @param {Integer} value - The field data value.
   * @param {Object} displayData - The entity view display.
   *
   * @return {String} The formatted field value.
   */
  formatTimeField(value, displayData) {
    return TimeInputHelper.processSecondsToTimeSelection(value, false);
  },

  /**
   * Format machine name to an allowed value.
   *
   * @param {String} key - The key value to look for.
   * @param {*} allowedVals - The listing of allowed values to apply.
   *
   * @return {String} The formatted value to return for the field.
   */
  retrieveAllowedValue(key, allowedVals) {
    let label = key;

    if (Array.isArray(allowedVals)) {
      const found = allowedVals.find((element) => element.value === key);
      if (found) {
        label = found.text;
      }
    } else if (allowedVals[key]) {
      label = allowedVals[key];
    }

    return label;
  },

  /**
   * Format a date and time string value into a standard date format.
   *
   * @param {*} value - Typically an ISO 8602 string.
   * @param {Object} displayData - The entity view display.
   *
   * @return {string} The formatted date and time.
   */
  formatDateTimeField(value, displayData) {
    const date = new Date(value);
    return RaftGenericHelper.formatDateTime(date);
  },
};
export default EntityViewDisplayFieldFormatters;
